import { CSV_ONLY_DOWNLOAD_ORG_CODES } from '../../domain/org'
import { TARGET_SEND_PARAMS } from '../../domain/sendParam'
import {
  SalesReportCalculationResult,
  SalesReportWithTenant,
} from '../../slices/services/api'
import { formatEightDigitDate } from '../../utils/date'
import { downloadBlob } from '../../utils/download'

// CSVのみダウンロード対象組織用の横持ちCSV生成（ヘッダー付き）
const generateCSVDataForCsvOnlyOrg = (
  orgCode: string,
  scId: string,
  salesReports: SalesReportWithTenant[]
): string => {
  if (!salesReports.length) {
    return ''
  }

  const sendParams = TARGET_SEND_PARAMS[orgCode]
  // ヘッダー行
  const header = ['salesDate', 'scId', 'tenantCode', ...sendParams].join(',')

  // データ行（1テナント1行の横持ち形式）
  const dataRows = salesReports.map((salesReport) => {
    const totals: { [sendParameterName: string]: number } = {}

    // 初期化
    sendParams.forEach((param) => {
      totals[param] = 0
    })

    // 各レジスターの計算結果を集計
    salesReport.registers.forEach((register) => {
      register.calculationResults.forEach((calcResult) => {
        const paramName = calcResult.calculationItem.sendParameterName
        if (sendParams.includes(paramName)) {
          if (calcResult.results && calcResult.results.definiteValue) {
            totals[paramName] += calcResult.results.definiteValue
          }
        }
      })
    })

    // 横持ち形式で1行を生成
    const values = sendParams.map((param) => totals[param])
    // tenantCodeは12桁左0詰め（サーバー実装と同様に数値変換してからフォーマット）
    const tenantCodeNum = parseInt(salesReport.tenant.code, 10)
    const paddedTenantCode = Number.isNaN(tenantCodeNum)
      ? salesReport.tenant.code
      : String(tenantCodeNum).padStart(12, '0')
    return [
      formatEightDigitDate(salesReport.date),
      scId,
      paddedTenantCode,
      ...values,
    ].join(',')
  })

  return [header, ...dataRows].join('\r\n')
}

export const generateCSVData = (
  orgCode: string,
  storeCode: string,
  salesReports: SalesReportWithTenant[],
  scId?: string
): string => {
  if (!salesReports.length) {
    return ''
  }

  // CSVのみダウンロード対象組織は横持ち形式
  if (CSV_ONLY_DOWNLOAD_ORG_CODES.includes(orgCode)) {
    if (!scId) {
      throw new Error('scId is required for CSV only download org codes')
    }
    return generateCSVDataForCsvOnlyOrg(orgCode, scId, salesReports)
  }

  // NOTE: ヘッダーはなしでデータのみを生成（縦持ち形式）
  const csvRows = salesReports.flatMap((salesReport) => {
    const totals: { [sendParameterName: string]: number } = {}

    salesReport.registers.forEach((register) => {
      let calcResults: SalesReportCalculationResult[]
      if (TARGET_SEND_PARAMS[orgCode]) {
        calcResults = register.calculationResults.filter((calcResult) =>
          TARGET_SEND_PARAMS[orgCode].includes(
            calcResult.calculationItem.sendParameterName
          )
        )
      } else {
        calcResults = register.calculationResults
      }

      calcResults.forEach((calcResult) => {
        totals[calcResult.calculationItem.sendParameterName] =
          totals[calcResult.calculationItem.sendParameterName] ?? 0

        if (calcResult.results && calcResult.results.definiteValue) {
          totals[calcResult.calculationItem.sendParameterName] +=
            calcResult.results.definiteValue
        }
      })
    })

    // NOTE: 営業日付,物件コード,店舗コード,報告項目コード,金額or数量
    return Object.entries(totals).map(
      ([key, value]) =>
        `${formatEightDigitDate(salesReport.date)},${storeCode},${
          salesReport.tenant.code
        },${key},${value}`
    )
  })

  return csvRows.join('\r\n')
}

export const downloadCSV = (
  orgCode: string,
  storeCode: string,
  date: string,
  salesReports: SalesReportWithTenant[],
  scId?: string
) => {
  const csvData = generateCSVData(orgCode, storeCode, salesReports, scId)
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' })

  // CSVのみダウンロード対象組織は{SCID}.csv形式、それ以外はNIPPOU_{date}.csv形式
  const filename =
    CSV_ONLY_DOWNLOAD_ORG_CODES.includes(orgCode) && scId
      ? `${scId}.csv`
      : `NIPPOU_${formatEightDigitDate(date)}.csv`

  downloadBlob(blob, filename)
}
